import React from 'react';
import { Menu, MessageSquare, PlayCircle } from 'lucide-react';
interface NavigationProps {
  currentPage: number;
  totalPages: number;
  onNavigate: (index: number) => void;
  onToggleParkingLot: () => void;
  parkingLotCount: number;
  onDemoJump: () => void;
}
const SECTIONS = [{
  label: 'Overview',
  start: 0,
  end: 1,
  time: 10,
  weight: 15,
  color: 'bg-green-200'
}, {
  label: 'You',
  start: 2,
  end: 4,
  time: 10,
  weight: 15,
  color: 'bg-green-300'
}, {
  label: 'Deal Review',
  start: 5,
  end: 7,
  time: 10,
  weight: 15,
  color: 'bg-sierra-blue'
}, {
  label: 'Demo',
  start: 8,
  end: 10,
  time: 20,
  weight: 30,
  color: 'bg-green-900'
}, {
  label: 'Close',
  start: 11,
  end: 14,
  time: 10,
  weight: 15,
  color: 'bg-gray-800'
}, {
  label: 'Appendix',
  start: 15,
  end: 19,
  time: 0,
  weight: 10,
  color: 'bg-gray-400'
}];
// Simplified top nav sections to fit
const TOP_NAV_SECTIONS = [{
  label: 'Overview',
  page: 0
}, {
  label: 'You',
  page: 2
}, {
  label: 'Deal Review',
  page: 5
}, {
  label: 'ACME Demo',
  page: 8
}, {
  label: 'Architecture',
  page: 11
}, {
  label: 'Proof',
  page: 12
}, {
  label: 'Close',
  page: 13
}, {
  label: 'Q&A',
  page: 14
}, {
  label: 'Appendix',
  page: 15
}];
export function Navigation({
  currentPage,
  totalPages,
  onNavigate,
  onToggleParkingLot,
  parkingLotCount,
  onDemoJump
}: NavigationProps) {
  // Calculate progress bar segments based on timeboxes (10/10/20/5/5)
  // We'll map the 50 mins to 100% width.
  const timeboxes = [{
    label: 'Intro & You',
    mins: 10,
    color: 'bg-blue-200'
  }, {
    label: 'Deal Review',
    mins: 10,
    color: 'bg-blue-300'
  }, {
    label: 'ACME Demo',
    mins: 20,
    color: 'bg-sierra-blue'
  }, {
    label: 'Close',
    mins: 5,
    color: 'bg-blue-800'
  }, {
    label: 'Q&A',
    mins: 5,
    color: 'bg-gray-800'
  }];
  return <div className="fixed top-0 left-0 right-0 z-40 bg-white/80 backdrop-blur-md border-b border-gray-100" data-id="element-34">
      {/* Progress Bar */}
      <div className="h-1 w-full flex" data-id="element-35">
        {timeboxes.map((box, i) => <div key={i} className={`h-full ${box.color} opacity-80`} style={{
        width: `${box.mins / 50 * 100}%`
      }} title={`${box.label} (${box.mins}m)`} data-id="element-36" />)}
      </div>

      <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between" data-id="element-37">
        <div className="flex items-center gap-6" data-id="element-38">
          <div className="flex items-center gap-2" data-id="element-39">
            <svg viewBox="0 0 116 36" className="h-6 text-sierra-blue fill-current" data-id="element-40">
              <path d="M15.8945 0C22.177 2.14224 22.5248 5.91514 22.0117 7.40625C23.0463 6.21674 26.4874 4.63099 31.4844 9C32.7697 15.511 29.6766 17.6992 28.1289 18C29.6765 18.3007 32.7698 20.4888 31.4844 27C26.4883 31.369 23.0463 29.7833 22.0117 28.5938C22.5248 30.0849 22.177 33.8578 15.8945 36C9.61215 33.8578 9.2643 30.0849 9.77734 28.5938C8.7428 29.7833 5.30171 31.369 0.304688 27C-0.980686 20.489 2.11249 18.3008 3.66016 18C2.11244 17.699 -0.980637 15.5107 0.304688 9C5.30171 4.63095 8.7428 6.21674 9.77734 7.40625C9.2643 5.91514 9.61215 2.14225 15.8945 0ZM15.8945 26.04C15.3881 27.7181 13.6496 29.0907 10.0264 28.417C10.8073 30.98 13.2035 32.7607 15.8936 32.7607H15.8945C18.5837 32.7607 20.9798 30.9791 21.7617 28.417C18.1385 29.0906 16.4001 27.7181 15.8945 26.04ZM3.9375 18.127C2.10902 20.085 1.7649 23.0507 3.10938 25.3799C4.45493 27.7091 7.19537 28.8941 9.80566 28.2891C7.41051 25.4882 7.72882 23.2964 8.92969 22.0195C7.22333 22.4206 5.16655 21.6014 3.93848 18.127H3.9375ZM27.8486 18.127C26.6205 21.6015 24.5638 22.4207 22.8574 22.0195C24.0583 23.2964 24.3766 25.4882 21.9814 28.2891C24.5918 28.8942 27.3331 27.7091 28.6777 25.3799H28.6768C30.0223 23.0507 29.6771 20.085 27.8486 18.127ZM22.5703 22.0039C20.3274 23.8831 19.0011 22.8524 18.4072 22.3516L18.4082 22.3525C18.5454 23.1173 18.7738 24.7807 16.0254 25.7832C17.291 26.9415 19.1887 27.1553 20.6826 26.293C22.1766 25.4304 22.9405 23.6792 22.5703 22.0039ZM9.2207 22.0029C8.85 23.6779 9.61462 25.4286 11.1084 26.291C12.6023 27.1534 14.5 26.9396 15.7656 25.7812C13.0164 24.7787 13.2456 23.1154 13.3828 22.3506C12.789 22.8523 11.4635 23.8819 9.2207 22.0029ZM95.6758 11.4932C98.1159 11.4932 99.908 13.1484 99.9082 15.376C99.9082 17.1865 98.7426 18.6681 97.0137 19.1426L98.6377 21.6484H98.6387C99.739 23.3463 100.245 23.8582 101.066 24.3994L101.048 24.5605C98.7185 25.16 97.7659 24.0676 97.3506 23.4268L94.6934 19.3271H92.3906V22.6182C92.3906 23.5953 92.6456 24.0376 93.293 24.3984L93.2734 24.5596H89.3711L89.3525 24.3984C90.0366 24.0376 90.292 23.5953 90.292 22.6182V13.4346C90.292 12.4574 90.0366 12.0151 89.3525 11.6543L89.3711 11.4932H95.6758ZM48.249 11.2949C49.7239 11.2949 50.6072 11.8054 51.2236 11.8057C51.404 11.8057 51.5664 11.7554 51.7158 11.6436L51.8398 11.6621L52.3877 14.917L52.2188 14.9854C51.0114 13.6665 49.6424 13.1133 48.2734 13.1133C46.9046 13.1133 45.9836 13.7538 45.9834 14.8359C45.9834 15.9813 47.0167 16.2984 48.2861 16.6348L49.5557 16.9766V16.9756C51.5596 17.5046 53.1395 18.4378 53.1396 20.8145C53.1396 23.1916 51.4278 24.7295 48.5469 24.7295C47.2258 24.7295 46.191 24.4722 45.2861 24.1875C44.4247 23.9291 44.2324 23.1122 44.0342 22.3691L43.6494 20.7402L43.8174 20.6719C45.2738 22.2335 47.0165 22.8438 48.5039 22.8438C49.9913 22.8437 50.9434 22.1094 50.9434 21.0205C50.9433 19.8076 49.7609 19.3346 48.5225 19.0293L47.2344 18.7002C45.2245 18.221 43.8555 17.1006 43.8555 15.0654C43.8556 12.7392 45.6172 11.295 48.249 11.2949ZM81.9502 11.4932C84.3902 11.4933 86.1815 13.1485 86.1816 15.376C86.1816 17.1865 85.0169 18.668 83.2881 19.1426L84.9121 21.6484H84.9131C85.692 22.8501 86.3942 23.7886 87.6758 24.3984L87.6572 24.5605C86.8491 24.7926 84.6717 25.0409 83.625 23.4268L80.9678 19.3271H78.665V22.6182C78.665 23.5953 78.92 24.0376 79.5674 24.3984L79.5479 24.5596H75.6455L75.627 24.3984C76.311 24.0376 76.5664 23.5953 76.5664 22.6182V13.4346C76.5664 12.4574 76.311 12.0151 75.627 11.6543L75.6455 11.4932H81.9502ZM113.766 22.7129H113.767C114.122 23.6211 114.457 24.0387 115.042 24.3994L115.036 24.5605H111.135L111.115 24.3994C111.78 24.0574 111.918 23.6644 111.588 22.7812L111.04 21.3506H105.664L105.135 22.7812C104.805 23.6644 104.942 24.0574 105.614 24.3994L105.595 24.5605H101.756L101.749 24.3994C102.333 24.0386 102.67 23.6213 103.031 22.7129L106.615 13.5098C106.951 12.6266 106.746 12.0527 105.949 11.6797L105.968 11.4932H109.459L113.766 22.7129ZM60.0254 11.6543C59.3095 12.0151 59.0361 12.4575 59.0361 13.4346V22.6182C59.0361 23.5953 59.3095 24.0377 60.0254 24.3984L60.0068 24.5596H55.9688L55.9492 24.3984C56.6774 24.0376 56.9395 23.5953 56.9395 22.6182V13.4346C56.9395 12.4574 56.6774 12.0151 55.9492 11.6543L55.9688 11.4932H60.0068L60.0254 11.6543ZM72.6455 14.3242L72.4844 14.3926C71.7496 13.6335 70.8596 13.3106 69.6279 13.3105H66V16.7578H69.1348C69.9865 16.7577 70.4711 16.5018 70.8262 15.9609L70.9883 15.9795V19.334L70.8262 19.3525C70.472 18.8116 69.9866 18.544 69.1348 18.5439H66V22.7432H70.0449V22.7422C71.2335 22.7422 72.1231 22.3751 72.9199 21.5605L73.0625 21.6289L72.3418 24.5596H62.9814L62.9629 24.3984C63.6469 24.0376 63.9023 23.5953 63.9023 22.6182V13.4346C63.9023 12.4574 63.6479 12.0151 62.9629 11.6543L62.9814 11.4932H71.9053L72.6455 14.3242ZM15.8955 12.0703C14.8102 12.0703 13.8121 12.8031 13.5293 13.9033C12.4356 13.599 11.3019 14.0957 10.7598 15.0352C10.2177 15.9746 10.3535 17.2051 11.1641 18C10.3532 18.7958 10.2176 20.0262 10.7598 20.9658C11.302 21.9053 12.4347 22.402 13.5293 22.0977C13.8131 23.1979 14.8102 23.9306 15.8955 23.9307C16.9808 23.9307 17.9789 23.1979 18.2617 22.0977C19.3554 22.402 20.489 21.9053 21.0312 20.9658C21.5734 20.0262 21.4379 18.7949 20.627 18C21.4375 17.2042 21.5733 15.9746 21.0312 15.0352C20.489 14.0957 19.3563 13.5989 18.2617 13.9033C17.9779 12.8031 16.9808 12.0703 15.8955 12.0703ZM9.09082 14.2217C7.45505 14.7384 6.31954 16.2751 6.31934 18C6.31934 19.726 7.4549 21.2625 9.09082 21.7793C8.58444 18.8978 10.1391 18.2631 10.8701 18C10.1389 17.7359 8.58546 17.1028 9.09082 14.2217ZM22.6973 14.2217C23.2036 17.103 21.6491 17.7369 20.918 18C21.649 18.264 23.2037 18.8977 22.6973 21.7793C24.3332 21.2625 25.4688 19.7251 25.4688 18C25.4685 16.2742 24.333 14.7384 22.6973 14.2217ZM18.2598 16.6348V19.3652L15.8936 20.7314L13.5273 19.3652V16.6348L15.8936 15.2686L18.2598 16.6348ZM106.354 19.5078H110.349L108.352 14.1807L106.354 19.5078ZM9.80664 7.71094C7.19633 7.10589 4.45497 8.29092 3.11035 10.6201C1.76493 12.9493 2.10999 15.915 3.93848 17.873L3.93945 17.874C5.16753 14.3986 7.2243 13.5793 8.93066 13.9805C7.7298 12.7036 7.41149 10.5118 9.80664 7.71094ZM28.6787 10.6191C27.3331 8.28992 24.5928 7.10487 21.9824 7.70996C24.3776 10.5108 24.0593 12.7026 22.8584 13.9795C24.5648 13.5783 26.6215 14.3985 27.8496 17.873C29.6781 15.915 30.0223 12.9493 28.6777 10.6201L28.6787 10.6191ZM78.6641 13.3164V17.4922H81.8438C83.082 17.4922 84.0154 16.5651 84.0156 15.3955C84.0156 14.2257 83.0822 13.3164 81.8438 13.3164H78.6641ZM92.3896 17.4922H95.5693C96.8076 17.4922 97.741 16.5651 97.7412 15.3955C97.7412 14.2257 96.8078 13.3164 95.5693 13.3164H92.3896V17.4922ZM20.6816 9.70996C19.1877 8.84758 17.2901 9.06136 16.0244 10.2197L16.0234 10.2207C18.7728 11.2232 18.5434 12.8866 18.4062 13.6514C19.0001 13.1496 20.3264 12.1199 22.5693 13.999C22.9405 12.3238 22.1757 10.5725 20.6816 9.70996ZM15.7646 10.2188C14.499 9.06045 12.6014 8.84656 11.1074 9.70898C9.61339 10.5715 8.85048 12.3228 9.2207 13.998C11.4636 12.119 12.789 13.1496 13.3828 13.6504L13.3818 13.6494C13.2447 12.8846 13.0164 11.2213 15.7646 10.2188ZM15.8945 3.24023C13.2054 3.24027 10.8092 5.02191 10.0273 7.58398C13.6506 6.91031 15.39 8.28285 15.8955 9.96094C16.401 8.28285 18.1395 6.9113 21.7627 7.58496C20.9818 5.02286 18.5856 3.24121 15.8955 3.24121L15.8945 3.24023Z" data-id="element-41" />
            </svg>
          </div>
          <div className="hidden md:flex items-center gap-1" data-id="element-42">
            <button onClick={onDemoJump} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-sierra-blue bg-green-50 hover:bg-green-100 rounded-full transition-colors" data-id="element-43">
              <PlayCircle className="w-4 h-4" data-id="element-44" />
              Jump to Demo
            </button>
          </div>
        </div>

        {/* Center: Section Nav */}
        <nav className="hidden md:flex items-center gap-6" data-id="element-45">
          {TOP_NAV_SECTIONS.map((section, idx) => <button key={idx} onClick={() => onNavigate(section.page)} className={`text-xs font-medium transition-colors ${currentPage >= section.page && (idx === TOP_NAV_SECTIONS.length - 1 || currentPage < TOP_NAV_SECTIONS[idx + 1].page) ? 'text-sierra-blue' : 'text-gray-400 hover:text-gray-700'}`} data-id="element-46">
              {section.label}
            </button>)}
        </nav>

        {/* Right: Actions */}
        <div className="flex items-center gap-3" data-id="element-47">
          <button onClick={onToggleParkingLot} className="relative p-2 text-gray-500 hover:bg-gray-50 rounded-full transition-colors" data-id="element-48">
            <MessageSquare className="w-5 h-5" data-id="element-49" />
            {parkingLotCount > 0 && <span className="absolute top-1 right-1 w-2 h-2 bg-amber-500 rounded-full border border-white" data-id="element-50" />}
          </button>
        </div>
      </div>
    </div>;
}